import { useState } from "react";
import type { Scenario, PlacedForce } from "@/lib/types";
import { FBDCanvas } from "./FBDCanvas";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FORCE_COLORS } from "./ForceArrow";
import { ChevronRight, Lightbulb, Eye, RotateCcw } from "lucide-react";

interface ScenarioViewProps {
	scenario: Scenario;
	scenarioNumber: number;
	totalScenarios: number;
	onComplete: (correct: boolean, attempts: number) => void;
	onNext: () => void;
	isLast: boolean;
}

export function ScenarioView({ scenario, scenarioNumber, totalScenarios, onComplete, onNext, isLast }: ScenarioViewProps) {
	const [showResult, setShowResult] = useState<null | "correct" | "incorrect">(null);
	const [showHint, setShowHint] = useState(false);
	const [showAnswer, setShowAnswer] = useState(false);
	const [showExplanation, setShowExplanation] = useState(false);
	const [attempts, setAttempts] = useState(0);
	const [completed, setCompleted] = useState(false);

	const handleCheck = (_placed: PlacedForce[], correct: boolean) => {
		const newAttempts = attempts + 1;
		setAttempts(newAttempts);

		if (correct) {
			setShowResult("correct");
			setShowExplanation(true);
			if (!completed) {
				setCompleted(true);
				onComplete(true, newAttempts);
			}
		} else {
			setShowResult("incorrect");
			// Auto-show hint after 2 wrong attempts
			if (newAttempts >= 2) {
				setShowHint(true);
			}
		}
	};

	const handleShowAnswer = () => {
		setShowAnswer(true);
		setShowExplanation(true);
		if (!completed) {
			setCompleted(true);
			onComplete(false, attempts);
		}
	};

	const handleRetry = () => {
		setShowResult(null);
		setShowHint(false);
		setShowAnswer(false);
		setShowExplanation(false);
		setAttempts(0);
		setCompleted(false);
	};

	const difficultyLabel = scenario.difficulty === 1 ? "Beginner" : scenario.difficulty === 2 ? "Intermediate" : "Advanced";
	const difficultyColor = scenario.difficulty === 1 ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : scenario.difficulty === 2 ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";

	return (
		<div className="space-y-4">
			{/* Scenario header */}
			<div className="flex items-start justify-between gap-4">
				<div>
					<div className="flex items-center gap-2 mb-1">
						<span className="text-xs text-muted-foreground font-medium">
							Scenario {scenarioNumber} of {totalScenarios}
						</span>
						<Badge variant="outline" className={difficultyColor}>
							{difficultyLabel}
						</Badge>
					</div>
					<h3 className="text-lg font-semibold">{scenario.title}</h3>
					<p className="text-sm text-muted-foreground mt-1">{scenario.description}</p>
				</div>
			</div>

			{/* Canvas */}
			<FBDCanvas scenario={scenario} onCheck={handleCheck} showResult={showResult} showAnswer={showAnswer} />

			{/* Hint */}
			{!showAnswer && (
				<div className="flex gap-2">
					<Button variant="ghost" size="sm" onClick={() => setShowHint(!showHint)}>
						<Lightbulb className="w-4 h-4 mr-1" />
						{showHint ? "Hide Hint" : "Show Hint"}
					</Button>
					{attempts >= 3 && !completed && (
						<Button variant="ghost" size="sm" onClick={handleShowAnswer}>
							<Eye className="w-4 h-4 mr-1" />
							Show Answer
						</Button>
					)}
				</div>
			)}

			{showHint && !showExplanation && (
				<Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950">
					<CardContent className="pt-4 pb-3">
						<p className="text-sm"><span className="font-medium">Hint:</span> {scenario.hint}</p>
					</CardContent>
				</Card>
			)}

			{/* Explanation */}
			{showExplanation && (
				<Card className={showResult === "correct" ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950" : "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950"}>
					<CardHeader className="pb-2 pt-4">
						<CardTitle className="text-sm">
							{showResult === "correct" ? "Excellent! Here's the full explanation:" : "Solution & Explanation:"}
						</CardTitle>
					</CardHeader>
					<CardContent className="pb-4 space-y-3">
						<p className="text-sm">{scenario.explanation}</p>
						<div className="space-y-1.5">
							<p className="text-xs font-medium text-muted-foreground">Forces in this diagram:</p>
							{scenario.correctForces.map((f, i) => (
								<div key={i} className="flex items-start gap-2 text-xs">
									<span
										className="inline-block w-2.5 h-2.5 rounded-full mt-0.5 shrink-0"
										style={{ backgroundColor: FORCE_COLORS[f.type] }}
									/>
									<span>
										<strong>{f.label}</strong> &mdash; {f.description}
									</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Navigation */}
			{completed && (
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={handleRetry}>
						<RotateCcw className="w-4 h-4 mr-1" />
						Retry
					</Button>
					<Button size="sm" onClick={onNext} className="ml-auto">
						{isLast ? "Finish Lesson" : "Next Scenario"}
						<ChevronRight className="w-4 h-4 ml-1" />
					</Button>
				</div>
			)}
		</div>
	);
}
